import type { PowerQualityWarning } from "../services/pfCorrection";
import {
  INDETERMINATE_WARNING_CODES,
  INFORMATION_WARNING_CODES,
  REVIEW_REQUIRED_WARNING_CODES,
  WARNING_CODES,
  type PowerQualityWarningCode,
} from "../services/powerQualityContract";
import { StatusIcon } from "./StatusIcon";
import { GROUP_TONE } from "./statusTone";

// The warning panel of both power quality studies (EOS-11, A32). A warning carries a code
// and the engine's sentence; the groups are the backend's own severity sets
// (results.py: INDETERMINATE, REVIEW_REQUIRED, WARNING, information), so a code is listed
// where the engine's status rule counts it. Within a group the engine's order is kept.

type WarningGroup = "not-met" | "review-evidence" | "other" | "declared-evidence";

const NO_WARNINGS_TEXT = "No engineering warnings raised.";

// Exhaustive: a new code without a label is a compile error.
export const WARNING_CODE_LABELS: Record<PowerQualityWarningCode, string> = {
  PF_TARGET_NOT_ABOVE_PRESENT: "Target power factor not above the present",
  PF_TARGET_UNITY_OR_LEADING: "Target power factor is unity",
  LOAD_FIGURES_SOURCE_NOT_DECLARED: "Load figures without a source",
  SHORT_CIRCUIT_POWER_NOT_ESTABLISHED: "Short-circuit power not established",
  SHORT_CIRCUIT_SOURCE_NOT_DECLARED: "Short-circuit power without a source",
  RESONANCE_NEAR_CHARACTERISTIC_ORDER: "Resonance near a characteristic order",
  CAPACITOR_VOLTAGE_ABOVE_RATING: "Capacitor voltage above its rating",
  BANK_BELOW_REQUIREMENT: "Declared bank below the requirement",
  STEP_RESIDUAL_ABOVE_SMALLEST_STEP: "Step layout leaves a residual",
  LOAD_RUN_NOT_LINKED: "Load figures not linked to a load run",
  CHARACTERISTIC_ORDERS_NOT_DECLARED: "Characteristic orders not declared",
  STEPS_NOT_DECLARED: "Step sizes not declared",
  REACTOR_RATIO_NOT_DECLARED: "No detuned reactor declared",
  CAPACITOR_VOLTAGE_AT_NOMINAL_ONLY: "Capacitor voltage checked at nominal voltage only",
  BILLING_CONTEXT_INFORMATION_ONLY: "Billing context (information only)",
  DISPLACEMENT_PF_BASIS: "Displacement power factor basis",
  SPECTRUM_SOURCE_NOT_DECLARED: "Spectrum without a source",
  SPECTRUM_ORDER_REPEATED: "Harmonic order repeated in a load",
  SPECTRUM_EMPTY: "No harmonic component declared",
  DEMAND_CURRENT_NOT_ESTABLISHED: "Maximum demand current not established",
  DEMAND_CURRENT_SOURCE_NOT_DECLARED: "Demand current without a source",
  HARMONIC_LIMIT_NOT_ESTABLISHED: "Harmonic limit not established",
  HARMONIC_LIMIT_DECLARED_NOT_VERIFIED: "Limit declared, not verified",
  DISTORTION_ABOVE_LIMIT: "Distortion above the limit",
  IN_PHASE_SUMMATION_ASSUMED: "In-phase summation assumed",
  SOURCE_IMPEDANCE_AS_REACTANCE: "Source impedance taken as reactance",
};

function groupOf(code: string): WarningGroup {
  if ((INDETERMINATE_WARNING_CODES as readonly string[]).includes(code)) {
    return "not-met";
  }
  if ((REVIEW_REQUIRED_WARNING_CODES as readonly string[]).includes(code)) {
    return "review-evidence";
  }
  if ((WARNING_CODES as readonly string[]).includes(code)) {
    return "other";
  }
  if ((INFORMATION_WARNING_CODES as readonly string[]).includes(code)) {
    return "declared-evidence";
  }
  // A code this page does not know yet is shown among the warnings, never dropped.
  return "other";
}

export const WARNING_CODE_GROUPS: Record<PowerQualityWarningCode, WarningGroup> =
  Object.fromEntries(
    Object.keys(WARNING_CODE_LABELS).map((code) => [code, groupOf(code)]),
  ) as Record<PowerQualityWarningCode, WarningGroup>;

export const GROUP_ORDER: readonly { group: WarningGroup; title: string }[] = [
  { group: "not-met", title: "Cannot be computed" },
  { group: "review-evidence", title: "Review required (evidence)" },
  { group: "other", title: "Warnings" },
  { group: "declared-evidence", title: "Information" },
];

type PowerQualityWarningPanelProps = {
  warnings: PowerQualityWarning[];
};

export function PowerQualityWarningPanel({ warnings }: PowerQualityWarningPanelProps) {
  return (
    <section aria-label="Engineering warnings">
      <h3>Engineering warnings</h3>
      {warnings.length === 0 ? (
        <p data-no-warnings="true">{NO_WARNINGS_TEXT}</p>
      ) : (
        <div data-warning-count={warnings.length}>
          {GROUP_ORDER.map(({ group, title }) => {
            const members = warnings
              .map((warning, index) => ({ ...warning, key: `${warning.code}-${index}` }))
              .filter((warning) => groupOf(warning.code) === group);
            if (members.length === 0) {
              return null;
            }
            return (
              <section key={group} aria-label={title} data-warning-group={group}>
                <h4>
                  <StatusIcon tone={GROUP_TONE[group]} size={20} />
                  {title}
                </h4>
                <ul>
                  {members.map((warning) => (
                    <li key={warning.key} data-warning-code={warning.code}>
                      <strong>{WARNING_CODE_LABELS[warning.code] ?? warning.code}</strong>
                      {": "}
                      {/* The engine's own sentence, kept as it was sent. */}
                      <span>{warning.message}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default PowerQualityWarningPanel;
